import { useContext, useState } from 'react';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

import { SocketContext } from '../context/SocketContext';
import { serverApi } from '../api';
import { setFavicon } from '../helpers/setFavicon';
import { guardarColoresStorage } from '../helpers/theme';

const MySwal = withReactContent(Swal);
const sweet = (title, text, icon) => { MySwal.fire({ title, text, icon }); };

export const RegistroUsuario = ({ onLoginSuccess }) => {
    const [step, setStep] = useState(1);

    const [formData, setFormData] = useState({
        usuario: '',
        password: '',
    });

    const [entidades, setEntidades] = useState([]);
    const [ubicaciones, setUbicaciones] = useState([]);
    const [modulos, setModulos] = useState([]);

    const [entidadSeleccionada, setEntidadSeleccionada] = useState(null);
    const [ubicacionSeleccionada, setUbicacionSeleccionada] = useState(null);

    const [loading, setLoading] = useState(false);
    const { conectarSocket } = useContext(SocketContext);

    const [filtroUbicacion, setFiltroUbicacion] = useState('');
    const [filtroModulo, setFiltroModulo] = useState('');

    const ubicacionesFiltradas = ubicaciones.filter(ubi =>
        `${ubi.nombreUbicacion || ''} ${ubi.codigoUbicacion || ''}`
            .toLowerCase()
            .includes(filtroUbicacion.toLowerCase())
    );

    const modulosFiltrados = modulos.filter(mod =>
        `${mod.moduloClase || ''} ${mod.modulo || ''}`
            .toLowerCase()
            .includes(filtroModulo.toLowerCase())
    );

    const handleInputChange = (e) => {
        setFormData({
            ...formData,
            [e.target.id]: e.target.value
        });
    };

    // --- STEP 1: Validar Usuario y Contraseña ---
    const verificarCredenciales = async (e) => {
        e.preventDefault();
        if (!formData.usuario || !formData.password) {
            return sweet('Atención', 'Ingrese usuario y contraseña', 'warning');
        }

        setLoading(true);
        try {
            const resp = await serverApi.post(`/auth/select-entidad`, formData);

            if (resp.data.ok && resp.data.entidades.length > 0) {
                setEntidades(resp.data.entidades);

                if (resp.data.entidades.length === 1) {
                    seleccionarEntidad(resp.data.entidades[0]);
                } else {
                    setStep(2);
                }
            } else {
                sweet('Atención!', 'Credenciales incorrectas o no tiene empresas asignadas', 'error');
            }
        } catch (error) {
            console.error("Error validando credenciales", error);
            sweet('Error', 'No se pudo conectar al servidor', 'error');
        } finally {
            setLoading(false);
        }
    };

    // --- STEP 2: Seleccionar Entidad (Empresa) ---
    const seleccionarEntidad = async (entidad) => {
        setEntidadSeleccionada(entidad);
        setLoading(true);
        try {
            const resp = await serverApi.post(`/auth/select-ubicacion-pantallas`, { codigoEntidad: entidad.codigoEntidad });
            if (resp.data.ok && resp.data.ubicaciones.length > 0) {
                setUbicaciones(resp.data.ubicaciones);

                if (resp.data.ubicaciones.length === 1) {
                    seleccionarUbicacion(entidad, resp.data.ubicaciones[0]);
                } else {
                    setStep(3);
                }
            } else {
                sweet('Atención!', 'No se encontraron sedes para esta empresa', 'warning');
            }
        } catch (error) {
            console.error("Error obteniendo ubicaciones", error);
            sweet('Error', 'No se pudieron obtener las sedes', 'error');
        } finally {
            setLoading(false);
        }
    };

    // --- STEP 3: Seleccionar Ubicación (Sede) ---
    const seleccionarUbicacion = async (entidadActual, ubicacion) => {
        setUbicacionSeleccionada(ubicacion);
        setLoading(true);
        try {
            const resp = await serverApi.post(`/auth/select-modulo`, {
                codigoEntidad: entidadActual.codigoEntidad,
                codigoUbicacion: ubicacion.codigoUbicacion,
                usuario: formData.usuario,
            });

            if (resp.data.ok) {
                setModulos(resp.data.modulos);
                setStep(4);
            }
        } catch (error) {
            console.error("Error obteniendo módulos", error);
            sweet('Error', 'No se pudieron obtener los módulos', 'error');
        } finally {
            setLoading(false);
        }
    };

    // --- STEP 4: Seleccionar Módulo y Login ---
    const seleccionarModuloYLogin = async (uidModulo) => {
        setLoading(true);
        const loginData = {
            usuario: formData.usuario,
            password: formData.password,
            codigoEntidad: entidadSeleccionada.codigoEntidad,
            codigoUbicacion: ubicacionSeleccionada.codigoUbicacion,
            uidModulo: uidModulo
        };

        try {
            const resp = await serverApi.post(`/auth/login`, loginData);
            if (resp.data.ok === false || !resp.data.login) {
                setLoading(false);
                return sweet('Atención!', 'Error al iniciar sesión', 'error');
            }

            const datos = resp.data.login[0];
            const token = resp.data.token;

            let datosPantallas = "";
            const respPantallas = await serverApi.post(`/auth/rol-Pantallas`, { codigoEntidad: datos.codigoEntidad, usuario: datos.usuario });
            if (respPantallas.data.ok && respPantallas.data.pantallas) {
                datosPantallas = respPantallas.data.pantallas;
                localStorage.setItem('pantallas', respPantallas.data.pantallas.map(item => item.codigoPantalla));
            }

            localStorage.setItem('token', token);
            localStorage.setItem('uidUsuario', datos.uidUsuario);
            localStorage.setItem('usuario', datos.usuario);
            localStorage.setItem('codigoEntidad', datos.codigoEntidad);
            localStorage.setItem('nombreEntidad', entidadSeleccionada?.nombreEntidad || datos.nombreEntidad || '');
            localStorage.setItem('agente', datos.nombreCompleto || datos.usuario);
            localStorage.setItem('codigoUbicacion', ubicacionSeleccionada.codigoUbicacion);
            localStorage.setItem('uidModulo', uidModulo);
            localStorage.setItem('modulo', datos.modulo ? `${datos.moduloClase} ${datos.modulo}` : 'Sin Módulo');
            localStorage.setItem('imagenLogo', datos.imagenLogo || '');
            localStorage.setItem('imagenIcono', datos.imagenIcono || '');
            setFavicon(datos.imagenIcono || datos.imagenLogo);
            guardarColoresStorage(datos.colorPrimario, datos.colorSecundario);

            conectarSocket({
                query: {
                    codigoEntidad: datos.codigoEntidad,
                    ubicacion: ubicacionSeleccionada?.codigoUbicacion,
                    uidUsuario: datos.uidUsuario,
                    uidModulo: uidModulo,
                    usuario: datos.usuario,
                    agente: datos.nombreCompleto || datos.usuario
                }
            });

            fetchTiposAtencion(datos.codigoEntidad, uidModulo, datos.uidUsuario, ubicacionSeleccionada);

            if (onLoginSuccess) {
                onLoginSuccess();
            }

        } catch (error) {
            console.error("Error en login final", error);
            sweet('Error', 'Revisa la conexión o credenciales', 'error');
            setLoading(false);
        }
    };

    const fetchTiposAtencion = async (codigoEntidad, uidModulo, uidUsuario, codigoUbicacion) => {
        const formData = {
            codigoEntidad,
            uidModulo,
            uidUsuario,
            codigoUbicacion
        };
        try {
            const resp = await serverApi.post(`/tipos-atencion/busca-por-modulo`, formData);
            const aten = await resp.data.atenciones.filter((item) => item.checked === true);
            localStorage.setItem('tiposAtencion', aten.map((item) => item.uidTipoAtencion));
        } catch (error) {
            console.error("Error al obtener tipos de atención", error);
        }
    };

    const volver = () => {
        if (step > 1) setStep(step - 1);
    };

    const stepsInfo = [
        { num: 1, label: 'Credenciales' },
        { num: 2, label: 'Empresa' },
        { num: 3, label: 'Sede' },
        { num: 4, label: 'Módulo' },
    ];

    return (
        <div className="login-container d-flex align-items-center justify-content-center p-3" style={{ minHeight: "100vh", backgroundColor: '#f4f7f6' }}>
            <div className="col-12 col-md-8 col-lg-5">
                <div className="card shadow-lg border-0 rounded-4 overflow-hidden" style={{ backgroundColor: '#ffffff' }}>
                    {/* Header institucional */}
                    <div className="p-4 text-white text-center" style={{ background: 'linear-gradient(135deg, var(--color-primario) 0%, var(--color-primario-oscuro) 100%)' }}>
                        <div className="d-inline-flex align-items-center justify-content-center mb-2 rounded-circle" style={{ width: 60, height: 60, background: 'rgba(255,255,255,0.15)' }}>
                            <i className="fa-solid fa-ticket fs-2" style={{ color: 'var(--color-secundario)' }}></i>
                        </div>
                        <h3 className="fw-bold m-0" style={{ letterSpacing: '0.5px' }}>TicketsApp</h3>
                        <p className="mb-0 opacity-75 small">Sistema Integrado de Gestión de Turnos</p>
                    </div>

                    {/* Stepper horizontal */}
                    <div className="px-4 pt-3 pb-2 border-bottom bg-light">
                        <div className="d-flex justify-content-between align-items-center position-relative">
                            {stepsInfo.map((st) => {
                                const isDone = step > st.num;
                                const isCurrent = step === st.num;
                                return (
                                    <div key={st.num} className="d-flex flex-column align-items-center z-1" style={{ width: '25%' }}>
                                        <div
                                            className="rounded-circle d-flex align-items-center justify-content-center fw-bold transition-all"
                                            style={{
                                                width: 32,
                                                height: 32,
                                                fontSize: 13,
                                                backgroundColor: isDone ? 'var(--color-secundario)' : isCurrent ? 'var(--color-primario)' : '#e2e8f0',
                                                color: isDone || isCurrent ? '#ffffff' : '#64748b',
                                                boxShadow: isCurrent ? '0 0 0 4px rgba(var(--color-primario-rgb), 0.2)' : 'none',
                                                transition: 'all 0.3s ease'
                                            }}
                                        >
                                            {isDone ? <i className="fa-solid fa-check fs-6"></i> : st.num}
                                        </div>
                                        <span className="small mt-1 text-center" style={{ fontSize: 11, fontWeight: isCurrent ? 700 : 500, color: isCurrent ? 'var(--color-primario)' : '#64748b' }}>
                                            {st.label}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div className="p-4">
                        <div className="text-center mb-4">
                            <h5 className="fw-bold" style={{ color: 'var(--color-primario-texto)' }}>
                                {step === 1 && "Iniciar Sesión"}
                                {step === 2 && "Selecciona tu Empresa"}
                                {step === 3 && "Selecciona tu Sede"}
                                {step === 4 && "Selecciona tu Módulo o Área"}
                            </h5>
                            <p className="text-muted small">
                                {step === 1 && "Ingresa tu usuario y contraseña institucional"}
                                {step === 2 && "Elige la empresa o entidad donde prestarás servicio"}
                                {step === 3 && "Elige la sede u oficina de atención"}
                                {step === 4 && "Selecciona el módulo o ventanilla de trabajo"}
                            </p>
                        </div>

                        {/* --- STEP 1 --- */}
                        {step === 1 && (
                            <form onSubmit={verificarCredenciales}>
                                <div className="mb-3">
                                    <label className="form-label small fw-bold text-secondary">Usuario</label>
                                    <div className="input-group">
                                        <span className="input-group-text bg-light border-end-0">
                                            <i className="fa-solid fa-user" style={{ color: 'var(--color-primario-texto)' }}></i>
                                        </span>
                                        <input
                                            type="text"
                                            className="form-control border-start-0 ps-0 shadow-none"
                                            id="usuario"
                                            placeholder="Ej: edgar.rodriguez"
                                            value={formData.usuario}
                                            onChange={handleInputChange}
                                            autoComplete="off"
                                            style={{ backgroundColor: '#fff' }}
                                        />
                                    </div>
                                </div>
                                <div className="mb-4">
                                    <label className="form-label small fw-bold text-secondary">Contraseña</label>
                                    <div className="input-group">
                                        <span className="input-group-text bg-light border-end-0">
                                            <i className="fa-solid fa-lock" style={{ color: 'var(--color-primario-texto)' }}></i>
                                        </span>
                                        <input
                                            type="password"
                                            className="form-control border-start-0 ps-0 shadow-none"
                                            id="password"
                                            placeholder="••••••••"
                                            value={formData.password}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                </div>
                                <button
                                    type="submit"
                                    className="btn w-100 py-3 fw-bold text-white shadow-sm rounded-3 transition-all"
                                    disabled={loading}
                                    style={{ background: 'linear-gradient(135deg, var(--color-primario) 0%, var(--color-primario-oscuro) 100%)', border: 'none' }}
                                >
                                    {loading ? <span className="spinner-border spinner-border-sm me-2"></span> : null}
                                    {loading ? 'Verificando...' : 'Continuar →'}
                                </button>
                            </form>
                        )}

                        {/* --- STEP 2 --- */}
                        {step === 2 && (
                            <div>
                                <div className="d-grid gap-3" style={{ maxHeight: 340, overflowY: 'auto' }}>
                                    {entidades.map(ent => (
                                        <div
                                            key={ent.codigoEntidad}
                                            className="card border-2 shadow-sm rounded-3 p-3 cursor-pointer border-hover"
                                            style={{ borderColor: '#e2e8f0', cursor: 'pointer', transition: 'all 0.2s ease' }}
                                            onClick={() => seleccionarEntidad(ent)}
                                        >
                                            <div className="d-flex align-items-center">
                                                <div className="rounded-3 p-3 me-3" style={{ backgroundColor: '#e6f4f2', color: 'var(--color-primario-texto)' }}>
                                                    <i className="fa-solid fa-building fs-4"></i>
                                                </div>
                                                <div className="flex-grow-1">
                                                    <h6 className="fw-bold mb-1" style={{ color: 'var(--color-primario-oscuro)' }}>
                                                        {ent.nombreEntidad || ent.codigoEntidad}
                                                    </h6>
                                                    <span className="badge bg-light text-secondary border">Código: {ent.codigoEntidad}</span>
                                                </div>
                                                <i className="fa-solid fa-chevron-right text-muted"></i>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <button onClick={volver} className="btn btn-link text-secondary mt-3 w-100 text-decoration-none small">
                                    ← Regresar al paso anterior
                                </button>
                            </div>
                        )}

                        {/* --- STEP 3 --- */}
                        {step === 3 && (
                            <div>
                                <div className="input-group mb-3 shadow-sm rounded-3">
                                    <span className="input-group-text bg-white border-end-0">
                                        <i className="fa-solid fa-magnifying-glass text-muted"></i>
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control border-start-0 shadow-none"
                                        placeholder="Buscar sede por nombre o código..."
                                        value={filtroUbicacion}
                                        onChange={(e) => setFiltroUbicacion(e.target.value)}
                                    />
                                </div>

                                <div className="d-flex justify-content-between align-items-center mb-2 px-1">
                                    <small className="text-muted fw-bold">{ubicacionesFiltradas.length} sedes disponibles</small>
                                </div>

                                <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                                    {ubicacionesFiltradas.map(ubi => (
                                        <div
                                            key={ubi.codigoUbicacion}
                                            className="card border-0 shadow-sm mb-2 rounded-3 cursor-pointer"
                                            style={{ backgroundColor: '#f8fafc', borderLeft: '4px solid var(--color-primario)', cursor: 'pointer' }}
                                            onClick={() => seleccionarUbicacion(entidadSeleccionada, ubi)}
                                        >
                                            <div className="card-body p-3 d-flex align-items-center justify-content-between">
                                                <div>
                                                    <h6 className="mb-1 fw-bold text-dark">
                                                        <i className="fa-solid fa-location-dot me-2" style={{ color: 'var(--color-secundario)' }}></i>
                                                        {ubi.nombreUbicacion}
                                                    </h6>
                                                    <small className="text-muted">Sede: {ubi.codigoUbicacion}</small>
                                                </div>
                                                <i className="fa-solid fa-arrow-right text-muted"></i>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <button onClick={volver} className="btn btn-link text-secondary mt-3 w-100 text-decoration-none small">
                                    ← Regresar
                                </button>
                            </div>
                        )}

                        {/* --- STEP 4 --- */}
                        {step === 4 && (
                            <div>
                                <div className="input-group mb-3 shadow-sm rounded-3">
                                    <span className="input-group-text bg-white border-end-0">
                                        <i className="fa-solid fa-magnifying-glass text-muted"></i>
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control border-start-0 shadow-none"
                                        placeholder="Buscar módulo o consultorio..."
                                        value={filtroModulo}
                                        onChange={(e) => setFiltroModulo(e.target.value)}
                                    />
                                </div>

                                <button
                                    className="btn btn-outline-secondary w-100 py-2 mb-3 fw-bold rounded-3 border-dashed"
                                    onClick={() => seleccionarModuloYLogin('none')}
                                    disabled={loading}
                                    style={{ borderStyle: 'dashed' }}
                                >
                                    <i className="fa-solid fa-tv me-2" style={{ color: 'var(--color-primario-texto)' }}></i>
                                    Sin Módulo (Modo Visualizador / Pantalla)
                                </button>

                                <div className="row g-2" style={{ maxHeight: 260, overflowY: 'auto' }}>
                                    {modulosFiltrados.map(mod => (
                                        <div key={mod.uidModulo} className="col-12 col-md-6">
                                            <div
                                                className="card h-100 border-0 shadow-sm rounded-3 cursor-pointer"
                                                style={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', cursor: 'pointer' }}
                                                onClick={() => seleccionarModuloYLogin(mod.uidModulo)}
                                            >
                                                <div className="card-body p-3 d-flex align-items-center">
                                                    <div
                                                        className="me-2 rounded-3 d-flex align-items-center justify-content-center"
                                                        style={{ width: 42, height: 42, background: '#e6f4f2', color: 'var(--color-primario-texto)', flexShrink: 0 }}
                                                    >
                                                        <i className="fa-solid fa-desktop"></i>
                                                    </div>
                                                    <div className="text-truncate">
                                                        <h6 className="mb-0 fw-bold text-dark text-truncate" style={{ fontSize: 14 }}>
                                                            {mod.moduloClase}
                                                        </h6>
                                                        <small className="text-muted" style={{ fontSize: 12 }}>
                                                            {mod.modulo}
                                                        </small>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <button onClick={volver} className="btn btn-link text-secondary mt-3 w-100 text-decoration-none small">
                                    ← Regresar
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Resumen inferior */}
                    {(entidadSeleccionada || ubicacionSeleccionada) && (
                        <div className="px-4 py-2 border-top bg-light d-flex flex-wrap gap-2 justify-content-between align-items-center small">
                            {entidadSeleccionada && (
                                <span className="badge rounded-pill bg-white text-secondary border px-3 py-2">
                                    <i className="fa-solid fa-building me-1" style={{ color: 'var(--color-primario-texto)' }}></i>
                                    {entidadSeleccionada.nombreEntidad}
                                </span>
                            )}
                            {ubicacionSeleccionada && (
                                <span className="badge rounded-pill bg-white text-secondary border px-3 py-2">
                                    <i className="fa-solid fa-location-dot me-1" style={{ color: 'var(--color-secundario)' }}></i>
                                    {ubicacionSeleccionada.nombreUbicacion}
                                </span>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
